import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
  Chip,
  IconButton,
  Skeleton,
  Alert,
  Button,
  Tabs,
  Tab,
  Collapse,
  MenuItem,
  TextField,
} from '@mui/material';
import Container from '@/components/PageSheet';
import RefreshIcon from '@mui/icons-material/Refresh';
import TuneIcon from '@mui/icons-material/Tune';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import JalaliDateField from '@/components/JalaliDateField';
import { userApi } from '@/services/api';
import { parseSharedDoc, docDate } from '@/utils/clientDocs';
import SharedDocCard from '@/pages/SharedDocCard';

// Tab → which document types it collects. Mirrors the clinic client-page tabs.
const TAB_TYPES = {
  all: null,
  consultation: ['consultation'],
  treatment_plan: ['treatment_plan'],
  treatment_record: ['treatment_record'],
  follow_up: ['follow_up', 'follow_up_schedule'],
  note: ['note'],
};
const TAB_ORDER = ['all', 'consultation', 'treatment_plan', 'treatment_record', 'follow_up', 'note'];

export default function NotesPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [tab, setTab] = useState('all');
  const [filterOpen, setFilterOpen] = useState(false);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [clinic, setClinic] = useState('all');

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['my-notes'],
    queryFn: () => userApi.myNotes(),
  });

  const allDocs = useMemo(
    () => (data?.notes || []).map(parseSharedDoc).sort((a, b) => new Date(docDate(b)) - new Date(docDate(a))),
    [data],
  );
  const clinics = useMemo(() => Array.from(new Set(allDocs.map((d) => d.clinicName).filter(Boolean))), [allDocs]);

  // Date + clinic filter (applied before tab filtering).
  const filtered = useMemo(() => allDocs.filter((d) => {
    const dd = String(docDate(d)).slice(0, 10);
    if (start && dd < start) return false;
    if (end && dd > end) return false;
    if (clinic !== 'all' && d.clinicName !== clinic) return false;
    return true;
  }), [allDocs, start, end, clinic]);

  const typeOf = (d) => (d.kind === 'doc' ? d.type : 'note');
  const inTab = (d, key) => key === 'all' || TAB_TYPES[key].includes(typeOf(d));
  const countFor = (key) => filtered.filter((d) => inTab(d, key)).length;
  const shown = filtered.filter((d) => inTab(d, tab));

  const activeFilters = (start ? 1 : 0) + (end ? 1 : 0) + (clinic !== 'all' ? 1 : 0);
  const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(lang === 'fa' ? 'fa-IR' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '');

  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: 'background.default' }}>
      <Container maxWidth="sm" sx={{ py: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight={800}>{t('notes.title')}</Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Button size="small" startIcon={<TuneIcon />} onClick={() => setFilterOpen((o) => !o)}
              color={activeFilters ? 'primary' : 'inherit'}>
              {t('notes.filter.title')}{activeFilters ? ` (${activeFilters})` : ''}
            </Button>
            <IconButton size="small" onClick={() => refetch()} disabled={isFetching} aria-label={t('notes.refresh')}>
              <RefreshIcon fontSize="small" sx={isFetching ? { animation: 'spin 1s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } } : undefined} />
            </IconButton>
          </Stack>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{t('notes.subtitle')}</Typography>

        {/* Filter — collapsed by default */}
        <Collapse in={filterOpen} unmountOnExit>
          <Card variant="outlined" sx={{ borderRadius: 3, mb: 1.5 }}>
            <CardContent>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1}>
                  <JalaliDateField label={t('notes.filter.from')} value={start} onChange={setStart} max={end || undefined} />
                  <JalaliDateField label={t('notes.filter.to')} value={end} onChange={setEnd} min={start || undefined} />
                </Stack>
                <TextField select size="small" fullWidth label={t('notes.filter.clinic')} value={clinic} onChange={(e) => setClinic(e.target.value)}>
                  <MenuItem value="all">{t('notes.filter.allClinics')}</MenuItem>
                  {clinics.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
                {activeFilters > 0 && (
                  <Button size="small" onClick={() => { setStart(''); setEnd(''); setClinic('all'); }}>
                    {t('notes.filter.clear')}
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Collapse>

        {/* Category tabs */}
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ mb: 2, minHeight: 0, '& .MuiTab-root': { minHeight: 38, textTransform: 'none', fontWeight: 700, minWidth: 0, px: 1.5 } }}
        >
          {TAB_ORDER.map((key) => {
            const c = countFor(key);
            const label = key === 'all' ? t('notes.filter.allTab') : t(`docs.types.${key}`);
            return <Tab key={key} value={key} label={c ? `${label} (${c})` : label} />;
          })}
        </Tabs>

        {isError && (
          <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={() => refetch()}>{t('notes.retry')}</Button>}>
            {t('notes.error')}
          </Alert>
        )}
        {isLoading ? (
          <Stack spacing={1.5}>{[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={110} />)}</Stack>
        ) : shown.length === 0 ? (
          <Card elevation={0} sx={{ borderRadius: 3, border: '1px dashed', borderColor: 'divider', textAlign: 'center', py: 5 }}>
            <DescriptionOutlinedIcon sx={{ fontSize: 42, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">{t('notes.empty')}</Typography>
          </Card>
        ) : (
          <Stack spacing={1.5}>
            {shown.map((d) => (
              d.kind === 'doc' ? (
                <SharedDocCard key={d.id} doc={d} clinicName={d.clinicName} dateLabel={fmt(docDate(d))} />
              ) : (
                <Card key={d.id} elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                      <DescriptionOutlinedIcon color="primary" fontSize="small" />
                      <Box sx={{ flex: 1 }} />
                      {d.category && <Chip size="small" variant="outlined" label={t(`notes.category.${d.category}`, d.category)} />}
                    </Stack>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>{d.body}</Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
                      <LocalHospitalIcon sx={{ fontSize: 14 }} />
                      <Typography variant="caption">{d.clinicName}</Typography>
                      <Typography variant="caption" sx={{ ms: 'auto' }}>· {fmt(docDate(d))}</Typography>
                    </Stack>
                  </CardContent>
                </Card>
              )
            ))}
          </Stack>
        )}
      </Container>
    </Box>
  );
}
